/**
 * 渠道管理页面
 * 管理员可以创建、编辑、查看、启用/禁用、测试渠道
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CHANNEL_OPTIONS,
  type Channel,
  type CreateChannelRequest,
  ChannelStatus,
  ChannelType,
  createChannel,
  deleteChannel,
  disableChannel,
  enableChannel,
  formatBalance,
  getChannel,
  getChannelList,
  getChannelStatusInfo,
  getChannelTypeInfo,
  getDefaultModels,
  getGroupList,
  getModelList,
  getSecretPrompt,
  MODEL_MAPPING_EXAMPLE,
  testAllChannels,
  testChannel,
  updateAllChannelsBalance,
  updateChannel,
  updateChannelBalance,
  verifyJSON,
} from "@/services/channelApi";
import {
  ArrowUpDown,
  CheckCircle,
  Edit,
  Play,
  Plus,
  Power,
  RefreshCw,
  Search,
  Trash2,
  Zap,
} from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

// 每页显示数量
const ITEMS_PER_PAGE = 10;

// 排序类型
type SortKey = "id" | "name" | "type" | "status" | "response_time" | "balance" | "group" | "weight";

export function ChannelManagementPage() {
  // 渠道列表状态
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDesc, setSortDesc] = useState(true);

  // 选中的渠道
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  // 对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // 操作状态
  const [testingChannel, setTestingChannel] = useState<number | null>(null);
  const [testingAll, setTestingAll] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState<number | null>(null);
  const [updatingAllBalance, setUpdatingAllBalance] = useState(false);

  // 模型和分组选项
  const [modelOptions, setModelOptions] = useState<{ id: string }[]>([]);
  const [basicModels, setBasicModels] = useState<string[]>([]);
  const [fullModels, setFullModels] = useState<string[]>([]);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [modelSearchKeyword, setModelSearchKeyword] = useState("");

  // 编辑表单状态
  const [isEdit, setIsEdit] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [customModel, setCustomModel] = useState("");
  const [formData, setFormData] = useState<{
    id?: number;
    type: ChannelType;
    name: string;
    key: string;
    base_url: string;
    other: string;
    models: string[];
    groups: string[];
    model_mapping: string;
    weight: number;
  }>({
    type: ChannelType.OPENAI,
    name: "",
    key: "",
    base_url: "",
    other: "",
    models: [],
    groups: ["default"],
    model_mapping: "",
    weight: 0,
  });

  // 加载渠道列表
  const loadChannels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getChannelList({ page: page - 1, keyword: keyword || undefined });
      if (response.success) {
        setChannels(response.data);
      } else {
        toast.error(response.message || "加载渠道列表失败");
      }
    } catch (error: any) {
      toast.error("加载渠道列表失败: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [page, keyword]);

  // 加载模型和分组选项
  const loadOptions = async () => {
    try {
      const [modelsRes, groupsRes] = await Promise.all([
        getModelList().catch(() => ({ success: true, data: [] as { id: string }[] })),
        getGroupList().catch(() => ({ success: true, data: ["default"] })),
      ]);

      if (modelsRes.success && modelsRes.data) {
        setModelOptions(modelsRes.data);
        // 基础模型：gpt-3 或 text- 开头的模型（与 OneAPI 一致）
        setBasicModels(
          modelsRes.data
            .filter((model) => model.id.startsWith("gpt-3") || model.id.startsWith("text-"))
            .map((model) => model.id)
        );
        // 全部模型
        setFullModels(modelsRes.data.map((model) => model.id));
      }
      if (groupsRes.success) {
        setGroupOptions(groupsRes.data || ["default"]);
      }
    } catch (error) {
      console.error("加载选项失败:", error);
    }
  };

  useEffect(() => {
    loadChannels();
    loadOptions();
  }, [loadChannels]);

  // 搜索处理
  const handleSearch = () => {
    setPage(1);
    loadChannels();
  };

  // 排序处理
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  // 获取排序后的渠道列表
  const getSortedChannels = () => {
    if (!sortKey) return channels;

    return [...channels].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDesc
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
    }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDesc ? bValue - aValue : aValue - bValue;
      }

      return 0;
    });
  };

  // 获取分页后的渠道列表
  const getPaginatedChannels = () => {
    const sorted = getSortedChannels();
    const start = (page - 1) * ITEMS_PER_PAGE;
    return sorted.slice(start, start + ITEMS_PER_PAGE);
  };

  // 打开创建对话框
  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({
      type: ChannelType.OPENAI,
          name: "",
      key: "",
      base_url: "",
      other: "",
      models: [],
      groups: ["default"],
      model_mapping: "",
          weight: 0,
    });
    setBatchMode(false);
    setEditDialogOpen(true);
  };

  // 打开编辑对话框
  const openEditDialog = async (channel: Channel) => {
    try {
      const response = await getChannel(channel.id);
      if (response.success && response.data) {
        const data = response.data;
        setIsEdit(true);
        setFormData({
          id: data.id,
          type: data.type,
          name: data.name,
          key: data.key || "",
          base_url: data.base_url || "",
          other: data.other || "",
          models: data.models ? data.models.split(",") : [],
          groups: data.group ? data.group.split(",") : ["default"],
          model_mapping: data.model_mapping
            ? JSON.stringify(JSON.parse(data.model_mapping), null, 2)
            : "",
          weight: data.weight || 0,
        });
        setEditDialogOpen(true);
      } else {
        toast.error("获取渠道详情失败");
      }
    } catch (error: any) {
      toast.error("获取渠道详情失败: " + error.message);
    }
  };

  // 处理表单输入变化
  const handleInputChange = (name: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      // 当类型变化且模型列表为空时，填入默认模型
      if (name === "type" && prev.models.length === 0) {
        newData.models = getDefaultModels(value);
      }

      return newData;
    });
  };

  // 添加自定义模型
  const addCustomModel = () => {
    if (!customModel.trim()) return;
    if (formData.models.includes(customModel)) return;

    setFormData((prev) => ({
      ...prev,
      models: [...prev.models, customModel],
    }));
    setCustomModel("");
  };

  // 提交表单
  const handleSubmit = async () => {
    // 验证
    if (!formData.name || !formData.key) {
      toast.error("请填写渠道名称和密钥！");
      return;
    }
    if (formData.models.length === 0) {
      toast.error("请至少选择一个模型！");
      return;
    }
    if (formData.model_mapping && !verifyJSON(formData.model_mapping)) {
      toast.error("模型映射必须是合法的 JSON 格式！");
      return;
    }

    // 处理数据
    const submitData: CreateChannelRequest = {
      type: formData.type,
      name: formData.name,
      key: formData.key,
      status: ChannelStatus.ENABLED,
      weight: formData.weight,
      base_url: formData.base_url.endsWith("/")
        ? formData.base_url.slice(0, -1)
        : formData.base_url,
      other: formData.other,
      models: formData.models.join(","),
      model_mapping: formData.model_mapping || "{}",
      group: formData.groups.join(","),
    };

    // 处理 Azure 默认版本
    if (formData.type === ChannelType.AZURE && !submitData.other) {
      submitData.other = "2023-06-01-preview";
    }

    // 处理讯飞默认版本
    if (formData.type === ChannelType.XUNFEI && !submitData.other) {
      submitData.other = "v2.1";
    }

    try {
      if (isEdit && formData.id) {
        const response = await updateChannel(formData.id, submitData);
        if (response.success) {
          toast.success("渠道更新成功！");
          setEditDialogOpen(false);
          loadChannels();
        } else {
          toast.error(response.message || "更新渠道失败");
        }
      } else {
        const response = await createChannel(submitData);
        if (response.success) {
          toast.success("渠道创建成功！");
          setEditDialogOpen(false);
        loadChannels();
      } else {
        toast.error(response.message || "创建渠道失败");
        }
      }
    } catch (error: any) {
      toast.error((isEdit ? "更新" : "创建") + "渠道失败: " + error.message);
    }
  };

  // 删除渠道
  const handleDeleteChannel = async () => {
    if (!selectedChannel) return;

    try {
      const response = await deleteChannel(selectedChannel.id);
      if (response.success) {
        toast.success("渠道删除成功");
        setDeleteDialogOpen(false);
        loadChannels();
      } else {
        toast.error(response.message || "删除渠道失败");
      }
    } catch (error: any) {
      toast.error("删除渠道失败: " + error.message);
    }
  };

  // 启用/禁用渠道
  const handleToggleStatus = async () => {
    if (!selectedChannel) return;

    try {
      const response =
        selectedChannel.status === ChannelStatus.ENABLED
          ? await disableChannel(selectedChannel.id)
          : await enableChannel(selectedChannel.id);

      if (response.success) {
        toast.success(
          selectedChannel.status === ChannelStatus.ENABLED ? "渠道已禁用" : "渠道已启用"
        );
        setStatusDialogOpen(false);
        loadChannels();
      } else {
        toast.error(response.message || "操作失败");
      }
    } catch (error: any) {
      toast.error("操作失败: " + error.message);
    }
  };

  // 测试单个渠道
  const handleTestChannel = async (channelId: number) => {
    try {
      setTestingChannel(channelId);
      const response = await testChannel(channelId);
      if (response.success) {
        toast.success(`测试成功，耗时 ${response.time?.toFixed(2)} 秒`);
        loadChannels();
      } else {
        toast.error(response.message || "测试失败");
      }
    } catch (error: any) {
      toast.error("测试失败: " + error.message);
    } finally {
      setTestingChannel(null);
    }
  };

  // 测试所有渠道
  const handleTestAllChannels = async () => {
    try {
      setTestingAll(true);
      const response = await testAllChannels();
      if (response.success) {
        toast.success("已开始测试所有已启用渠道，请稍后刷新查看结果");
      } else {
        toast.error(response.message || "测试失败");
      }
    } catch (error: any) {
      toast.error("测试失败: " + error.message);
    } finally {
      setTestingAll(false);
    }
  };

  // 更新单个渠道余额
  const handleUpdateBalance = async (channelId: number) => {
    try {
      setUpdatingBalance(channelId);
      const response = await updateChannelBalance(channelId);
      if (response.success) {
        toast.success("余额更新成功");
        loadChannels();
      } else {
        toast.error(response.message || "更新余额失败");
      }
    } catch (error: any) {
      toast.error("更新余额失败: " + error.message);
    } finally {
      setUpdatingBalance(null);
    }
  };

  // 更新所有渠道余额
  const handleUpdateAllBalance = async () => {
    try {
      setUpdatingAllBalance(true);
      const response = await updateAllChannelsBalance();
      if (response.success) {
        toast.success("已更新所有已启用渠道余额");
        loadChannels();
      } else {
        toast.error(response.message || "更新失败");
      }
    } catch (error: any) {
      toast.error("更新失败: " + error.message);
    } finally {
      setUpdatingAllBalance(false);
    }
  };

  // 渲染响应时间
  const renderResponseTime = (responseTime: number) => {
    if (responseTime === 0) {
      return <Badge variant="outline">未测试</Badge>;
    }

    const time = responseTime / 1000;
    const text = `${time.toFixed(2)} 秒`;

    if (responseTime <= 1000) {
      return <Badge className="bg-green-500 hover:bg-green-600">{text}</Badge>;
    } else if (responseTime <= 3000) {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">{text}</Badge>;
    } else if (responseTime <= 5000) {
      return <Badge className="bg-orange-500 hover:bg-orange-600">{text}</Badge>;
    } else {
      return <Badge variant="destructive">{text}</Badge>;
    }
  };

  // 渲染排序表头
  const renderSortableHeader = (key: SortKey, label: string) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <ArrowUpDown className="h-4 w-4" />
      </div>
    </TableHead>
  );

  const paginatedChannels = getPaginatedChannels();
  const totalPages = Math.ceil(channels.length / ITEMS_PER_PAGE);

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        {/* 头部 */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">渠道管理</h1>
            <p className="text-muted-foreground">
              管理和配置 AI 服务渠道
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            添加渠道
          </Button>
        </div>

        {/* 统计卡片 */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">总渠道数</CardTitle>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{channels.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">已启用</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {channels.filter((c) => c.status === ChannelStatus.ENABLED).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">已禁用</CardTitle>
              <Power className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {channels.filter((c) => c.status === ChannelStatus.DISABLED).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 操作栏 */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索渠道 ID、名称或密钥..."
                    className="pl-8 w-80"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <Button variant="outline" size="sm" onClick={handleSearch}>
                  搜索
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestAllChannels}
                  disabled={testingAll}
                >
                  <Play className="mr-2 h-4 w-4" />
                  测试所有渠道
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUpdateAllBalance}
                  disabled={updatingAllBalance}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${updatingAllBalance ? "animate-spin" : ""}`} />
                  更新所有余额
                </Button>
                <Button variant="outline" size="sm" onClick={loadChannels} disabled={loading}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  刷新
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 渠道列表 */}
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  {renderSortableHeader("id", "ID")}
                  {renderSortableHeader("name", "名称")}
                  {renderSortableHeader("group", "分组")}
                  {renderSortableHeader("type", "类型")}
                  {renderSortableHeader("status", "状态")}
                  {renderSortableHeader("response_time", "响应时间")}
                  {renderSortableHeader("balance", "余额")}
                  {renderSortableHeader("weight", "优先级")}
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      加载中...
                    </TableCell>
                  </TableRow>
                ) : paginatedChannels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      暂无渠道数据
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedChannels.map((channel) => {
                    const typeInfo = getChannelTypeInfo(channel.type);
                    const statusInfo = getChannelStatusInfo(channel.status);

                    return (
                      <TableRow key={channel.id}>
                        <TableCell className="font-medium">{channel.id}</TableCell>
                        <TableCell>{channel.name || "无"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{channel.group || "default"}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" style={{ borderColor: typeInfo.color }}>
                            {typeInfo.text}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={channel.status === ChannelStatus.ENABLED ? "default" : "secondary"}
                          >
                            {statusInfo.text}
                          </Badge>
                        </TableCell>
                        <TableCell>{renderResponseTime(channel.response_time)}</TableCell>
                        <TableCell
                          className="cursor-pointer hover:text-primary"
                          onClick={() => handleUpdateBalance(channel.id)}
                          title="点击更新余额"
                        >
                          {formatBalance(channel.type, channel.balance)}
                        </TableCell>
                        <TableCell>{channel.weight}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTestChannel(channel.id)}
                              disabled={testingChannel === channel.id}
                              title="测试"
                            >
                              <Play className={`h-4 w-4 ${testingChannel === channel.id ? "animate-pulse" : ""}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateBalance(channel.id)}
                              disabled={updatingBalance === channel.id}
                              title="更新余额"
                            >
                              <Zap className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(channel)}
                              title="编辑"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedChannel(channel);
                                setStatusDialogOpen(true);
                              }}
                              title={channel.status === ChannelStatus.ENABLED ? "禁用" : "启用"}
                            >
                              <Power className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedChannel(channel);
                                setDeleteDialogOpen(true);
                              }}
                              title="删除"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  共 {channels.length} 条记录，第 {page} / {totalPages} 页
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 创建/编辑渠道对话框 */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? "编辑渠道" : "添加渠道"}</DialogTitle>
            <DialogDescription>
              {isEdit ? "修改渠道配置信息" : "创建新的 AI 服务渠道"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* 渠道类型 */}
            <div>
              <Label htmlFor="type">渠道类型 *</Label>
              <Select
                value={formData.type.toString()}
                onValueChange={(value) => handleInputChange("type", Number.parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择渠道类型" />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((option) => (
                    <SelectItem key={option.key} value={option.value.toString()}>
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Azure 特殊配置 */}
            {formData.type === ChannelType.AZURE && (
              <>
                <div className="p-3 bg-yellow-50 rounded-md text-sm text-yellow-800">
                  注意，<strong>模型部署名称必须和模型名称保持一致</strong>，因为 One API 会把请求体中的 model 参数替换为你的部署名称。
            </div>
            <div>
                  <Label htmlFor="base_url">AZURE_OPENAI_ENDPOINT *</Label>
              <Input
                    id="base_url"
                    value={formData.base_url}
                    onChange={(e) => handleInputChange("base_url", e.target.value)}
                    placeholder="例如：https://docs-test-001.openai.azure.com"
              />
            </div>
            <div>
                  <Label htmlFor="other">默认 API 版本</Label>
              <Input
                    id="other"
                    value={formData.other}
                    onChange={(e) => handleInputChange("other", e.target.value)}
                    placeholder="例如：2023-06-01-preview"
              />
            </div>
              </>
            )}

            {/* 自定义渠道配置 */}
            {formData.type === ChannelType.CUSTOM && (
            <div>
                <Label htmlFor="base_url">Base URL *</Label>
              <Input
                id="base_url"
                  value={formData.base_url}
                  onChange={(e) => handleInputChange("base_url", e.target.value)}
                  placeholder="例如：https://openai.justsong.cn"
              />
            </div>
            )}

            {/* 讯飞配置 */}
            {formData.type === ChannelType.XUNFEI && (
            <div>
                <Label htmlFor="other">模型版本</Label>
              <Input
                  id="other"
                  value={formData.other}
                  onChange={(e) => handleInputChange("other", e.target.value)}
                  placeholder="例如：v2.1"
                />
              </div>
            )}

            {/* FastGPT 配置 */}
            {formData.type === ChannelType.FASTGPT && (
              <div>
                <Label htmlFor="base_url">私有部署地址</Label>
                <Input
                  id="base_url"
                  value={formData.base_url}
                  onChange={(e) => handleInputChange("base_url", e.target.value)}
                  placeholder="例如：https://fastgpt.run/api/openapi"
                />
              </div>
            )}

            {/* 渠道名称 */}
            <div>
              <Label htmlFor="name">渠道名称 *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="请为渠道命名"
              />
            </div>

            {/* 分组 */}
            <div>
              <Label>分组 *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {groupOptions.map((group) => (
                  <div key={group} className="flex items-center space-x-2">
                    <Checkbox
                      id={`group-${group}`}
                      checked={formData.groups.includes(group)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          handleInputChange("groups", [...formData.groups, group]);
                        } else {
                          handleInputChange(
                            "groups",
                            formData.groups.filter((g) => g !== group)
                          );
                        }
                      }}
                    />
                    <label htmlFor={`group-${group}`} className="text-sm">
                      {group}
                    </label>
            </div>
                ))}
              </div>
            </div>

            {/* 模型选择 */}
            <div>
              <Label>模型 *</Label>
              {/* 已选模型标签 */}
              {formData.models.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 mb-2 p-2 border rounded-md bg-muted/30">
                  {formData.models.map((model) => (
                    <Badge
                      key={model}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                      onClick={() =>
                        handleInputChange(
                          "models",
                          formData.models.filter((m) => m !== model)
                        )
                      }
                    >
                      {model} ×
                    </Badge>
                  ))}
                </div>
              )}
              {/* 快捷按钮 */}
              <div className="flex flex-wrap gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleInputChange("models", basicModels)}
                >
                  填入基础模型
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleInputChange("models", fullModels)}
                >
                  填入所有模型
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleInputChange("models", getDefaultModels(formData.type))}
                >
                  填入默认模型
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleInputChange("models", [])}
                >
                  清除所有模型
                </Button>
              </div>
              {/* 自定义模型输入 */}
              <div className="flex space-x-2 mb-2">
              <Input
                  value={customModel}
                  onChange={(e) => setCustomModel(e.target.value)}
                  placeholder="输入自定义模型名称"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomModel();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addCustomModel}>
                  填入
                </Button>
              </div>
              {/* 可选模型列表 */}
              {modelOptions.length > 0 && (
                <div className="border rounded-md">
                  <div className="p-2 border-b">
                    <Input
                      value={modelSearchKeyword}
                      onChange={(e) => setModelSearchKeyword(e.target.value)}
                      placeholder="搜索模型..."
                      className="h-8"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                    {modelOptions
                      .filter((m) =>
                        modelSearchKeyword
                          ? m.id.toLowerCase().includes(modelSearchKeyword.toLowerCase())
                          : true
                      )
                      .map((model) => (
                        <div key={model.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`model-${model.id}`}
                            checked={formData.models.includes(model.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleInputChange("models", [...formData.models, model.id]);
                              } else {
                                handleInputChange(
                                  "models",
                                  formData.models.filter((m) => m !== model.id)
                                );
                              }
                            }}
                          />
                          <label htmlFor={`model-${model.id}`} className="text-sm cursor-pointer">
                            {model.id}
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* 模型重定向 */}
            <div>
              <Label htmlFor="model_mapping">模型重定向</Label>
              <Textarea
                id="model_mapping"
                value={formData.model_mapping}
                onChange={(e) => handleInputChange("model_mapping", e.target.value)}
                placeholder={`此项可选，用于修改请求体中的模型名称，为一个 JSON 字符串，例如：\n${JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2)}`}
                className="font-mono text-sm min-h-[120px]"
              />
            </div>

            {/* 密钥 */}
            <div>
              <Label htmlFor="key">密钥 *</Label>
              {!isEdit && (
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="batch"
                    checked={batchMode}
                    onCheckedChange={(checked) => setBatchMode(!!checked)}
                  />
                  <label htmlFor="batch" className="text-sm">
                    批量创建（一行一个密钥）
                  </label>
                </div>
              )}
              {batchMode ? (
                <Textarea
                  id="key"
                  value={formData.key}
                  onChange={(e) => handleInputChange("key", e.target.value)}
                  placeholder="请输入密钥，一行一个"
                  className="font-mono text-sm min-h-[120px]"
                />
              ) : (
                <Input
                  id="key"
                  type="password"
                  value={formData.key}
                  onChange={(e) => handleInputChange("key", e.target.value)}
                  placeholder={getSecretPrompt(formData.type)}
                />
              )}
            </div>

            {/* 代理设置（非 Azure、自定义、FastGPT） */}
            {formData.type !== ChannelType.AZURE &&
              formData.type !== ChannelType.CUSTOM &&
              formData.type !== ChannelType.FASTGPT && (
                <div>
                  <Label htmlFor="base_url">代理</Label>
                  <Input
                    id="base_url"
                    value={formData.base_url}
                    onChange={(e) => handleInputChange("base_url", e.target.value)}
                    placeholder="此项可选，用于通过代理站来进行 API 调用，格式为：https://domain.com"
                  />
                </div>
              )}

            {/* 权重 */}
            <div>
              <Label htmlFor="weight">权重</Label>
              <Input
                id="weight"
                type="number"
                value={formData.weight}
                onChange={(e) => handleInputChange("weight", Number.parseInt(e.target.value) || 0)}
                placeholder="权重值，用于负载均衡"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleSubmit}>
              {isEdit ? "更新" : "创建"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除渠道 "{selectedChannel?.name}" 吗？此操作无法撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteChannel}>删除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 状态切换确认对话框 */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认操作</AlertDialogTitle>
            <AlertDialogDescription>
              确定要{selectedChannel?.status === ChannelStatus.ENABLED ? "禁用" : "启用"}渠道 "
              {selectedChannel?.name}" 吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus}>确认</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LayoutWithSidebar>
  );
}
